import React, { useState } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Stack,
  Typography,
  useTheme,
  Divider,
  MenuItem,
  TextField,
  Button,
  Popover,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { MoreHorizOutlined } from "@mui/icons-material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LanguageIcon from "@mui/icons-material/Language";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { useSelector } from "react-redux";
import { getRegularDate } from "utils/time.util";
import MediaBiasChart from "./BarGraph";
import { normalizeCollectionOverviewValue } from "../data/collection-overview-options";
import ProfileAvatar from "components/base/ProfileAvatar";
import GroupAvatar from "./GroupAvatar";
import { useIsTab } from "hooks/is-tab.hook";
import { useIsMobile } from "hooks/is-mobile.hook";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useParams } from "react-router-dom";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import { Helmet } from "react-helmet-async";

const Header = ({
  isPublic,
  collection,
  length,
  setAccessPopup,
  setFilter,
  filter,
  setEdit,
  setDeletePopup,
  collectionOverview,
  setName,
  onSort,
  sortCriteria,
  sortDirection,
  setCopyPopup,
  loading = false,
}) => {
  const [anchorMoreMenu, setAnchorMoreMenu] = useState(null);

  const [showSearch, setShowSearch] = useState(false);
  const { userPreferences } = useSelector((state) => state.account);
  const [selectedChart, setSelectedChart] = useState(
    normalizeCollectionOverviewValue(userPreferences?.defaultCollectionOverview)
  );

  const theme = useTheme();
  const isTab = useIsTab();
  const isMobile = useIsMobile();
  const { user } = useSelector((state) => state.auth);
  const { avatarProps } = useSelector((state) => state.data);

  const params = useParams();

  React.useEffect(() => {
    setSelectedChart(
      normalizeCollectionOverviewValue(userPreferences?.defaultCollectionOverview)
    );
  }, [params.id, userPreferences?.defaultCollectionOverview]);

  const categoryData = collectionOverview?.categoryData || null;

  const handleMoreMenuOpen = (event) => setAnchorMoreMenu(event.currentTarget);
  const handleMoreMenuClose = () => setAnchorMoreMenu(null);



  const handleMenuItemClick = (action) => {
    setAnchorMoreMenu(null);
    switch (action) {
      case "manageAccess":
        setAccessPopup(true);
        break;
      case "rename":
        setEdit(true);
        break;
      case "delete":
        setDeletePopup(true);
        break;
      default:
        break;
    }
  };

  const isPopoverOpen = Boolean(anchorMoreMenu);

  const getUsername = () => {
    if (collection.isShared_ysn) {
      return collectionOverview.collectionSharedWithUsersDisplay;
    }
    if (isPublic) {
      return collectionOverview.userName_chr;
    }

    return user.userName;
  };

  const handleCopyToClipboard = () => {
    const currentUrl = window.location.href;
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        // Should be Toast instead.
        // console.log("Link copied to clipboard");
      })
      .catch((error) => {
        console.error("Failed to copy the link:", error);
      });
    toast.success(
      `Link to ${collection.collectionName_chr} copied to clipboard!`
    );
  };

  return (
    <React.Fragment>
      {!loading && (
        <Helmet>
          <title>
            {`Sooth - ${isPublic ? "Public Collection" : "Collection"}${collection?.collectionName_chr
              ? ` - ${collection.collectionName_chr}`
              : ""
              }${isPublic && getUsername() ? ` by @${getUsername()}` : ""}`}
          </title>
        </Helmet>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {!isPublic ? (
          <Stack direction="row" gap={2} alignItems={"center"}>
            {loading ? (
              <>
                <Skeleton
                  variant="text"
                  width={isTab ? "60vw" : "30rem"}
                  height={48}
                />
                <Skeleton variant="circular" width={24} height={24} />
              </>
            ) : (
              <>
                <Typography
                  color="#5c6d7e"
                  alignItems="center"
                  variant="h3"
                  fontSize={"3rem"}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: { xs: "2.25rem", sm: "3.5rem" },
                    maxHeight: "7rem",
                    maxWidth: "95%",
                    flexShrink: 1,
                  }}
                >
                  {collection.collectionName_chr}
                </Typography>
                {collection.isShared_ysn && (
                  <AccessibleIconButton
                    title="Sharing"
                    sx={{ p: 0, height: 18, width: 18 }}
                    onClick={() => handleMenuItemClick("manageAccess")}
                  >
                    <PeopleOutlineIcon />
                  </AccessibleIconButton>
                )}
                {collection.isPublic_ysn && (
                  <AccessibleIconButton
                    title="Public"
                    sx={{ p: 0, height: 18, width: 18 }}
                    onClick={() => handleMenuItemClick("manageAccess")}
                  >
                    <LanguageIcon />
                  </AccessibleIconButton>
                )}
                <AccessibleIconButton
                  sx={{ p: 0, height: 18, width: 18 }}
                  onClick={handleMoreMenuOpen}
                  title="Collection Menu"
                >
                  <MoreHorizOutlined />
                </AccessibleIconButton>
                <Popover
                  anchorEl={anchorMoreMenu}
                  open={isPopoverOpen}
                  onClose={handleMoreMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  disableScrollLock={true}
                >
                  {collection.parentCollectionId === 0 && (
                    <MenuItem
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 1.5,
                        mt: 2,
                        width: "22rem",
                        "&:hover": {
                          backgroundColor: theme.palette.secondary.main,
                        },
                      }}
                      onClick={() => handleMenuItemClick("manageAccess")}
                    >
                      <PeopleOutlineIcon
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1">Manage Access</Typography>
                    </MenuItem>
                  )}
                  <MenuItem
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 1.5,
                      width: "22rem",
                      mt: collection.parentCollectionId !== 0 ? 2 : 0,
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.main,
                      },
                    }}
                    onClick={() => {
                      setName(collection.collectionName_chr);
                      handleMenuItemClick("rename");
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Typography>Rename</Typography>
                  </MenuItem>
                  <MenuItem
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 1.5,
                      mb: 2,
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.main,
                      },
                    }}
                    onClick={() => handleMenuItemClick("delete")}
                  >
                    <DeleteOutlineIcon
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Typography>Delete</Typography>
                  </MenuItem>
                </Popover>
              </>
            )}
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="space-between">
            <Stack alignItems="center" direction="row" gap={4}>
              {loading ? (
                <>
                  <Skeleton
                    variant="text"
                    width={isTab ? "60vw" : "30rem"}
                    height={48}
                  />
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="rectangular" width={120} height={36} />
                  <Skeleton variant="rectangular" width={150} height={36} />
                </>
              ) : (
                <>
                  <Typography variant="h3" color={"#5c6d7e"} fontSize={"3rem"}>
                    {collection.collectionName_chr}
                  </Typography>
                  <LanguageIcon />
                  <Tooltip title="Share this link">
                    <Button
                      onClick={handleCopyToClipboard}
                      startIcon={<InsertLinkIcon />}
                      variant="contained"
                      color="secondary"
                      sx={{
                        px: 2,
                      }}
                    >
                      Share
                    </Button>
                  </Tooltip>
                  <Button
                    onClick={() => {
                      setCopyPopup(true);
                    }}
                    startIcon={<ContentCopyIcon />}
                    variant="contained"
                    color="secondary"
                    sx={{
                      px: 2,
                    }}
                  >
                    Copy to Collection
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        )}
        <Stack direction="row" gap={2}>
          {loading ? (
            <Skeleton variant="rectangular" width={200} height={40} />
          ) : showSearch ? (
            <TextField
              value={filter}
              onBlur={() => setShowSearch(false)}
              placeholder="Search collections..."
              onChange={(e) => setFilter(e.target.value)}
              label="Search Collections"
              sx={{
                "& legend": {
                  backgroundColor: "transparent !important",
                  position: "absolute",
                },
              }}
              slotProps={{
                inputLabel: {
                  sx: visuallyHidden,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ height: 30, color: theme.palette.primary.dark }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: filter && (
                    <InputAdornment position="end">
                      <AccessibleIconButton
                        title="Clear Search"
                        onClick={() => setFilter("")}
                      >
                        <ClearIcon />
                      </AccessibleIconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : (
            <AccessibleIconButton
              title="Search Collection"
              sx={{ p: 0 }}
              onClick={() => setShowSearch(true)}
            >
              <SearchOutlinedIcon sx={{ color: theme.palette.primary.dark }} />
            </AccessibleIconButton>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          backgroundColor: theme.palette.secondary.main,
          p: 2,
          my: 4,
          borderRadius: 1,
          display: "grid",
          gridTemplateAreas: isMobile
            ? '"owners" "stats" "divide" "ratings"'
            : isTab
              ? selectedChart === "category"
                ? '"owners stats" "divide divide" "ratings ratings"'
                : '"owners" "stats" "divide" "ratings"'
              : '"owners divide ratings" "stats divide ratings"',
          gridTemplateColumns: isMobile
            ? "1fr"
            : isTab
              ? selectedChart === "category"
                ? "1fr 1fr"
                : "1fr"
              : selectedChart === "mediaBias"
                ? "1fr auto 1fr"
                : "2fr auto 8fr",
          width: {
            xs: "auto",
            sm: "100%",
          },
          mr: {
            xs: 2,
            sm: 0,
          },
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gridArea: "owners",
          }}
        >
          {loading ? (
            <>
              <Skeleton variant="circular" width={48} height={48} />
              <Skeleton variant="text" width={150} height={28} />
            </>
          ) : collection.isShared_ysn ? (
            <GroupAvatar
              userIds={collectionOverview.collectionSharedWithUserId}
              userAvatars={
                collectionOverview.collectionSharedWithUserIdByAvatarUrl
              }
            />
          ) : (
            <ProfileAvatar
              userId={isPublic ? collectionOverview.userId_chr : user.userId}
              avatarUrl={
                isPublic
                  ? collectionOverview.userAvatarUrl_chr
                  : avatarProps?.avatarImageUrl
              }
            />
          )}
          {!loading && <Typography variant="h6">@{getUsername()}</Typography>}
        </Stack>

        <Stack
          direction="row"
          gap={{ xs: 2, sm: 3 }}
          justifyContent={{ xs: "space-between", sm: "flex-start" }}
          sx={{
            gridArea: "stats",
          }}
        >
          {loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <Stack gap={1} key={`stats-skeleton-${idx}`}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={60} height={24} />
              </Stack>
            ))
          ) : (
            <>
              <Stack gap={1}>
                <Typography
                  variant="body2"
                  color={theme.palette.secondary.contrastText}
                >
                  Bookmarks
                </Typography>
                <Typography variant="body1">{length}</Typography>
              </Stack>
              <Stack gap={1}>
                <Typography
                  variant="body2"
                  color={theme.palette.secondary.contrastText}
                >
                  Created
                </Typography>
                <Typography variant="body1">
                  {getRegularDate(collectionOverview?.createdDateTime_dtm)}
                </Typography>
              </Stack>
              <Stack gap={1}>
                <Typography
                  variant="body2"
                  color={theme.palette.secondary.contrastText}
                >
                  Updated
                </Typography>
                <Typography variant="body1">
                  {getRegularDate(collectionOverview?.lastUpdatedDateTime_dtm)}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
        {!isTab && (
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              gridArea: "divide",
            }}
          />
        )}
        {isTab && (
          <Divider
            flexItem
            orientation="horizontal"
            sx={{
              gridArea: "divide",
            }}
          />
        )}

        {loading ? (
          <Skeleton
            variant="rectangular"
            height={isTab ? (selectedChart === "category" ? 400 : 250) : (selectedChart === "category" ? 160 : 120)}
            sx={{ gridArea: "ratings", borderRadius: 1 }}
          />
        ) : (
          <MediaBiasChart
            allSidesData={collectionOverview.allSidesData}
            categoryData={categoryData}
            selectedChart={selectedChart}
            setSelectedChart={setSelectedChart}
          />
        )}
      </Box>
    </React.Fragment>
  );
};

export default Header;
